"use client";

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { LucideIcon, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  fetchProjectById,
  fetchProjectIdFromCanvasId,
  fetchUsers,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { LabelBadge } from "../common/badges/label-badge";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { columnStatuses } from "../ProjectList/CanvasKanban";
import { User } from "@/types/users";

// TODO: Add in all linear related buttons
// const actionButtons: ActionButtonConfig[] = useMemo(
import { EstimateBadge } from "../common/badges/estimate-badge";
import { PriorityBadge } from "../common/badges/priority-badge";
import { StatusBadge } from "../common/badges/status-badge";
import { DueDateBadge } from "../common/badges/due-date-badge";
import { ShortcutProviderRoot } from "../providers/ShortcutProvider";
import AssigneeDropdown from "../common/issues/assignee-dropdown";
import { Project } from "@/types/issues";
import { useSession } from "@clerk/clerk-react";
import { LabelInterface } from "@/types/labels";
//   () => [
//
//     {
//       id: "backlog",
//       icon: CircleDashed,
//       text: "Backlog",
//       onClick: handleBacklogClick,
//     },
//     {
//       id: "priority",
//       icon: MinusIconLucide,
//       text: "Priority",
//       onClick: handlePriorityClick,
//     },
//     {
//       id: "estimate",
//       icon: Triangle,
//       text: "Estimate",
//       onClick: handleEstimateClick,
//     },
//     {
//       id: "due-date",
//       icon: Clock,
//       text: "Due date",
//       onClick: handleDueDateClick,
//     },
//   ],
//   [
//     handleBacklogClick,
//     handlePriorityClick,
//     handleEstimateClick,
//     handleDueDateClick,
//   ]
// );
export interface ActionButtonConfig {
  id: string;
  icon: LucideIcon;
  text: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface CustomRenderedButton {
  id: string;
  customRender: () => JSX.Element;
}

type ButtonItem = ActionButtonConfig | CustomRenderedButton;

const isCustomRenderedButton = (
  button: ButtonItem
): button is CustomRenderedButton => {
  return "customRender" in button;
};

interface ActionButtonsRowProps {
  buttons?: ActionButtonConfig[];
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  buttonWidth?: number;
  ellipsisWidth?: number;
  gapWidth?: number;
  buttonRefs?: React.MutableRefObject<Map<string, HTMLButtonElement | null>>;
}

interface ActionButtonProps {
  config: ActionButtonConfig;
  className?: string;
}

const ActionButton: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  ActionButtonProps
> = ({ config, className }, ref) => {
  const Icon = config.icon;
  return (
    <Button
      ref={ref}
      variant="outline"
      size="sm"
      onClick={config.onClick}
      disabled={config.disabled}
      className={cn(
        "flex items-center justify-center px-2 gap-1 text-xs h-7",
        "bg-background-primary border border-border-primary text-text-primary hover:bg-background-secondary",
        className
      )}
      data-button-id={config.id}
    >
      <Icon className="w-4 h-4" />
      {config.text}
    </Button>
  );
};

const ForwardedActionButton = React.forwardRef(ActionButton);
ForwardedActionButton.displayName = "ActionButton";

const MemoizedActionButton = React.memo(ForwardedActionButton);

export const ActionButtonsRow = React.memo(
  ({
    buttons,
    className,
    buttonClassName,
    dropdownClassName,
    buttonWidth: explicitButtonWidth,
    ellipsisWidth: explicitEllipsisWidth = 36,
    gapWidth = 8,
    buttonRefs: parentButtonRefs,
  }: ActionButtonsRowProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const internalButtonRefs = useRef<Map<string, HTMLButtonElement | null>>(
      new Map()
    );
    const buttonRefs = parentButtonRefs ?? internalButtonRefs;

    const fetchIssuesForProject = useIssuesStore(
      (state) => state.fetchIssuesForProject
    );
    const canvasId = useCanvasId();
    const currentProject = useActiveProject();
    const currentIssue = useIssuesStore(
      (state) => state.canvasIdToIssue[canvasId]
    );

    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
    const session = useSession();

    const [users, setUsers] = useState<User[] | null>(null);
    useEffect(() => {
      if (!currentProject?.id) return;
      if (!session) return;
      if (users) return;
      const fetchedUsers = fetchUsers(
        currentProject?.id ?? "",
        session.session ?? null
      );
      fetchedUsers.then((users) => {
        setUsers(users || []);
      });
    }, [currentProject?.id, session]);

    const fetchedProject = useMemo(async () => {
      if (!canvasId || !supabaseClient) {
        return null;
      }

      const project = fetchProjectIdFromCanvasId(canvasId, supabaseClient).then(
        (projectId) => {
          return fetchProjectById(projectId, supabaseClient).then((project) => {
            return project.data as Project;
          });
        }
      );

      await project;

      return project;
    }, [canvasId, supabaseClient]);

    useEffect(() => {
      if (currentProject && !currentIssue) {
        fetchIssuesForProject(supabaseClient, currentProject.id, false);
      }
    }, [currentProject, currentIssue]);

    const issueLabelInterfaces = useMemo(() => {
      if (!currentIssue) return [] as LabelInterface[];
      if (!currentProject) return [] as LabelInterface[];
      const projectLabels = currentProject?.labels;
      if (!projectLabels) return [] as LabelInterface[];
      const labelInterfaces = currentIssue.canvas.labels.map((l) => {
        return projectLabels?.find((pl) => pl.id === l) as LabelInterface;
      });
      return labelInterfaces || ([] as LabelInterface[]);
    }, [currentIssue, currentProject?.labels]);

    const renderLabelDropdown = useCallback(() => {
      if (!currentIssue) return null;
      return (
        <LabelBadge
          className="!h-7 bg-secondary-0 !text-text-primary text-xs !font-medium outline-1"
          issue={currentIssue}
          projectLabels={currentProject?.labels || []}
          labelsToStack={issueLabelInterfaces}
        />
      );
    }, [currentIssue, issueLabelInterfaces]);

    const renderEstimateBadge = useCallback(() => {
      if (!currentIssue) return null;
      return (
        <EstimateBadge
          issueId={currentIssue?.canvas.id}
          estimate={currentIssue?.featurePrd?.estimate}
          variant="text-dark"
        />
      );
    }, [currentIssue?.featurePrd?.estimate]);

    const renderPriorityBadge = useCallback(() => {
      if (!currentIssue) return null;
      return (
        <PriorityBadge
          issueId={currentIssue?.canvas.id}
          priority={currentIssue?.featurePrd?.priority}
          variant="text-dark"
        />
      );
    }, [currentIssue]);

    const renderStatusBadge = useCallback(() => {
      if (!currentIssue) return null;
      return (
        <StatusBadge
          issueId={currentIssue?.canvas.id ?? ""}
          status={
            columnStatuses.find(
              (s) => s.name === currentIssue.featurePrd?.status
            ) ?? columnStatuses[0]
          }
          statuses={columnStatuses}
          variant="text-dark"
        />
      );
    }, [currentIssue]);

    const renderDueDateBadge = useCallback(() => {
      if (!currentIssue) return null;

      return (
        <DueDateBadge
          issueId={currentIssue?.canvas.id ?? ""}
          dueDate={currentIssue?.featurePrd?.due_date}
          variant="text-dark"
          allowOverdueStyling={true}
        />
      );
    }, [currentIssue]);

    const renderAssignUserDropdown = useCallback(() => {
      if (!currentIssue) return null;

      return (
        <AssigneeDropdown
          className="!h-7 bg-secondary-0 !text-text-primary text-xs !font-medium outline-1"
          issueId={currentIssue?.canvas.id ?? ""}
          users={users || []}
          value={currentIssue?.canvas.assigned_user_id ?? null}
          variant="text-dark"
        />
      );
    }, [users, currentIssue]);

    const allButtons = useMemo(() => {
      const labelButton = {
        id: "labels-dropdown",
        customRender: renderLabelDropdown,
      };

      const estimateButton = {
        id: "estimate-badge",
        customRender: renderEstimateBadge,
      };

      const assignUserButton = {
        id: "assign-user-dropdown",
        customRender: renderAssignUserDropdown,
      };

      const priorityButton = {
        id: "priority-badge",
        customRender: renderPriorityBadge,
      };

      const statusButton = {
        id: "status-badge",
        customRender: renderStatusBadge,
      };

      const dueDateButton = {
        id: "due-date-badge",
        customRender: renderDueDateBadge,
      };

      return [
        assignUserButton,
        statusButton,
        priorityButton,
        estimateButton,
        labelButton,
        dueDateButton,
      ].filter(Boolean) as ButtonItem[];
    }, [
      buttons,
      fetchedProject,
      renderLabelDropdown,
      renderEstimateBadge,
      renderAssignUserDropdown,
      renderPriorityBadge,
      renderStatusBadge,
      renderDueDateBadge,
    ]);

    const [visibleCount, setVisibleCount] = useState(allButtons.length);
    const [buttonWidths, setButtonWidths] = useState<Map<string, number>>(
      new Map()
    );

    // Effect to measure initial button widths if not provided explicitly
    useEffect(() => {
      if (explicitButtonWidth) return; // Skip measurement if width is provided

      const widths = new Map<string, number>();
      let allMeasured = true;
      allButtons.forEach((button) => {
        const btnElement = buttonRefs.current.get(button.id);
        if (btnElement) {
          // Use offsetWidth for rendered width, add gap unless it's the first element
          widths.set(
            button.id,
            btnElement.offsetWidth + (widths.size > 0 ? gapWidth : 0)
          );
        } else {
          allMeasured = false; // Mark if any button wasn't measured yet
        }
      });

      // Only update if all buttons were measured to avoid partial state
      if (allMeasured && widths.size === allButtons.length) {
        setButtonWidths(widths);
      }
      // Rerun if button refs change (might happen on initial render)
    }, [allButtons, explicitButtonWidth, gapWidth]);

    // Effect for ResizeObserver
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const observer = new ResizeObserver((entries) => {
        const containerWidth = entries[0].contentRect.width;
        if (containerWidth === 0) return; // Skip if width is 0

        let requiredWidth = explicitEllipsisWidth; // Start with ellipsis button width
        let count = 0;

        for (const button of allButtons) {
          const width = explicitButtonWidth
            ? explicitButtonWidth + (count > 0 ? gapWidth : 0)
            : buttonWidths.get(button.id);
          if (!width) {
            // If width is missing (measurement failed or explicit width not set), assume a default or skip?
            // For now, let's assume a fallback width, though this might be inaccurate.
            const fallbackWidth = 80 + (count > 0 ? gapWidth : 0); // Example fallback
            if (requiredWidth + fallbackWidth <= containerWidth) {
              requiredWidth += fallbackWidth;
              count++;
            } else {
              break;
            }
          } else if (requiredWidth + width <= containerWidth) {
            requiredWidth += width;
            count++;
          } else {
            break; // Not enough space
          }
        }
        // Check if all buttons fit without ellipsis
        let allButtonsWidth = 0;
        allButtons.forEach((btn, index) => {
          allButtonsWidth +=
            (explicitButtonWidth
              ? explicitButtonWidth
              : buttonWidths.get(btn.id) ?? 80) + (index > 0 ? gapWidth : 0);
        });

        if (allButtonsWidth <= containerWidth) {
          setVisibleCount(allButtons.length); // All fit
        } else {
          setVisibleCount(count); // Only 'count' buttons fit before needing ellipsis
        }
      });

      observer.observe(container);
      return () => observer.disconnect();
    }, [
      allButtons,
      buttonWidths,
      explicitButtonWidth,
      explicitEllipsisWidth,
      gapWidth,
    ]);

    const visibleButtons = useMemo(
      () => allButtons.slice(0, visibleCount),
      [allButtons, visibleCount]
    );
    const hiddenButtons = useMemo(
      () => allButtons.slice(visibleCount),
      [allButtons, visibleCount]
    );

    const measurementStyles: React.CSSProperties = explicitButtonWidth
      ? {}
      : {
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          zIndex: -1,
        };

    return (
      <>
        <ShortcutProviderRoot>
          {!explicitButtonWidth && !parentButtonRefs && (
            <div
              style={measurementStyles}
              className={cn("flex flex-row align-middle", className)}
            >
              {allButtons.map((button) =>
                isCustomRenderedButton(button) ? (
                  <div
                    key={button.id}
                    ref={(el) => buttonRefs.current.set(button.id, el as any)}
                  >
                    {button.customRender()}
                  </div>
                ) : (
                  <MemoizedActionButton
                    key={button.id}
                    config={button}
                    className={buttonClassName}
                    ref={(el) => buttonRefs.current.set(button.id, el)}
                  />
                )
              )}
            </div>
          )}

          <div
            ref={containerRef}
            className={cn(
              "flex flex-row items-center gap-2 p-1 overflow-hidden align-middle",
              className
            )}
          >
            {visibleButtons.map((button) =>
              isCustomRenderedButton(button) ? (
                <React.Fragment key={button.id}>
                  {button.customRender()}
                </React.Fragment>
              ) : (
                <MemoizedActionButton
                  key={button.id}
                  config={button}
                  className={buttonClassName}
                  ref={(el) => buttonRefs.current.set(button.id, el)}
                />
              )
            )}

            {hiddenButtons.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "flex items-center justify-center w-auto px-2 h-7 text-xs",
                      "bg-background-primary border border-border-primary text-text-primary hover:bg-background-secondary",
                      dropdownClassName
                    )}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="flex flex-col gap-1">
                  {hiddenButtons.map((button) =>
                    isCustomRenderedButton(button) ? (
                      <React.Fragment key={button.id}>
                        {button.customRender()}
                      </React.Fragment>
                    ) : (
                      <DropdownMenuItem
                        key={button.id}
                        onClick={button.onClick}
                        disabled={button.disabled}
                        className="flex flex-col items-start gap-1"
                        style={{
                          justifyContent: "left",
                        }}
                      >
                        <div className="flex">
                          <button.icon className=" mr-2 h-4 w-4" />
                          <span>{button.text}</span>
                        </div>
                      </DropdownMenuItem>
                    )
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </ShortcutProviderRoot>
      </>
    );
  }
);
